/**
 * ChartPane (JSX)
 * ---------------
 * Hosts a Lightweight Charts candlestick chart and exposes helpers to map
 * chart coordinates → SVG pixels for overlays.
 *
 * Props
 * - height?: number (default 280)
 * - candles: Array<{time, open, high, low, close}>
 * - children?: (ctx) => ReactNode
 *     ctx = { overlay, timeToX, logicalToX, priceToY, hostEl }
 */

// import React, { useEffect, useRef, useState } from "react";
// import { createChart, CandlestickSeries } from "lightweight-charts";

// export default function ChartPane({ height = 280, candles, children }) {
//   const hostRef = useRef(null);
//   const [size, setSize] = useState({ w: 0, h: 0 });

//   useEffect(() => {
//     const el = hostRef.current;
//     if (!el) return;

//     let frame = 0;
//     const ro = new ResizeObserver((entries) => {
//       const entry = entries[0];
//       // measure immediately…
//       const cr = entry?.contentRect || el.getBoundingClientRect();
//       const nextW = Math.round(cr.width);
//       const nextH = Math.round(cr.height);

//       // …but set state on the next animation frame
//       cancelAnimationFrame(frame);
//       frame = requestAnimationFrame(() => {
//         setSize((prev) => {
//           if (prev.w === nextW && prev.h === nextH) return prev; // no-op -> no loop
//           return { w: nextW, h: nextH };
//         });
//       });
//     });

//     ro.observe(el);

//     return () => {
//       cancelAnimationFrame(frame);
//       ro.disconnect();
//     };
//   }, []);

//   // your existing canvas/overlay wiring here, just make sure you use `size.w/size.h`
//   return (
//     <div ref={hostRef} style={{ height }}>
//       {/* render chart + overlays with size { w: size.w, h: size.h } */}
//       {typeof children === "function"
//         ? children({
//             overlay: { w: size.w, h: size.h },
//             hostEl: hostRef.current,
//             // ...rest of your ctx (logicalToX, priceToY, etc)
//           })
//         : null}
//     </div>
//   );
// }

import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createChart, CandlestickSeries } from "lightweight-charts";

/* ---- Utilities ---- */
function normalizeCandles(input) {
  if (!Array.isArray(input)) return [];
  const rows = input
    .filter(Boolean)
    .map((c, i) => {
      const t = c.time ?? i + 1;
      const ts =
        typeof t === "string" || t instanceof Date
          ? Math.floor(new Date(t).getTime() / 1000)
          : Number.isFinite(t)
          ? t
          : i + 1;
      return {
        time: ts,
        open: +c.open,
        high: +c.high,
        low: +c.low,
        close: +c.close,
      };
    })
    .filter(
      (c) =>
        Number.isFinite(c.time) &&
        Number.isFinite(c.open) &&
        Number.isFinite(c.high) &&
        Number.isFinite(c.low) &&
        Number.isFinite(c.close)
    );

  rows.sort((a, b) => a.time - b.time);
  for (let i = 1; i < rows.length; i++) {
    if (rows[i].time <= rows[i - 1].time) rows[i].time = rows[i - 1].time + 1;
  }
  return rows;
}

/* ---- Component ---- */
export default function ChartPane({ height = 280, candles, children }) {
  const hostRef = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef(null);
  const [overlay, setOverlay] = useState({ w: 0, h: height });

  useLayoutEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    // Ensure a non-zero size before creating the chart
    const width = Math.max(1, host.clientWidth);

    const chart = createChart(host, {
      width,
      height,
      layout: { background: { color: "#0E0F13" }, textColor: "#DDE2EA" },
      grid: { vertLines: { color: "#1C2128" }, horzLines: { color: "#1C2128" } },
      timeScale: { timeVisible: true, borderVisible: false, rightOffset: 3 },
      rightPriceScale: { borderVisible: false },
    });

    // 🔧 Version-agnostic: prefer addCandlestickSeries; fallback to addSeries
    let series = null;
    if (typeof chart.addCandlestickSeries === "function") {
      series = chart.addCandlestickSeries({
        upColor: "#2ECC71",
        downColor: "#FF4C4C",
        wickUpColor: "#2ECC71",
        wickDownColor: "#FF4C4C",
        borderVisible: false,
      });
    } else if (typeof chart.addSeries === "function") {
      series = chart.addSeries(CandlestickSeries, {
        upColor: "#2ECC71",
        downColor: "#FF4C4C",
        wickUpColor: "#2ECC71",
        wickDownColor: "#FF4C4C",
        borderVisible: false,
      });
    } else {
      console.warn("[ChartPane] This lightweight-charts build has no candlestick series API.");
    }

    chartRef.current = chart;
    seriesRef.current = series;

    // Initial overlay size
    const r = host.getBoundingClientRect();
    setOverlay({ w: Math.round(r.width), h: height });

    // Debounced resize observer
    let raf = 0;
    const ro = new ResizeObserver(() => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const w = Math.max(1, host.clientWidth);
        chart.applyOptions({ width: w, height });
        setOverlay({ w, h: height });
      });
    });
    ro.observe(host);

    return () => {
      if (raf) cancelAnimationFrame(raf);
      try { ro.disconnect(); } catch {}
      try { chart.remove(); } catch {}
      chartRef.current = null;
      seriesRef.current = null;
    };
  }, [height]);

  // Feed data safely
  useEffect(() => {
    const s = seriesRef.current;
    if (!s) return;
    const rows = normalizeCandles(candles);
    s.setData(rows);              // safe even if rows === []
    try { chartRef.current?.timeScale().fitContent(); } catch {}
  }, [candles]);

  // Helpers for overlays
  const timeToX = (t) => {
    const ts = chartRef.current?.timeScale();
    return ts?.timeToCoordinate?.(t) ?? null;
  };
  const logicalToX = (l) => {
    const ts = chartRef.current?.timeScale();
    return ts?.logicalToCoordinate?.(l) ?? null;
  };
  const priceToY = (p) => seriesRef.current?.priceToCoordinate?.(p) ?? null;

  return (
    <div style={{ position: "relative", width: "100%", borderRadius: 16 }}>
      <div
        ref={hostRef}
        style={{
          width: "100%",
          height,
          borderRadius: 14,
          overflow: "hidden",
          background: "#0E0F13",
        }}
      />
      {typeof children === "function" &&
        overlay.w > 0 &&
        children({ overlay, timeToX, logicalToX, priceToY, hostEl: hostRef.current })}
    </div>
  );
}
